// Common Bread Problems
interface BreadProblem {
  name: string;
  visualIdentifiers: string[];
  mainCauses: string[];
  solutions: string[];
}

const BREAD_PROBLEMS: BreadProblem[] = [
  {
    name: 'Dense Crumb',
    visualIdentifiers: ['Heavy texture', 'lack of open holes', 'gummy regions'],
    mainCauses: [
      'Issue with fermentation management',
      'Insufficient starter activity',
      'Issue between hydration and flour choice',
      'Excess pressure during shaping. Not enough bubbles left in the dough',
      'Excess dough strength / gluten network too strong'
    ],
    solutions: [
      'Adjust total fermentation time, adjust storage temperature (either room or fridge) or adjust starter proportion in the dough. These parameters will either speed up or slow down fermentation. Aim for a 6-7hrs total fermentation time at normal room temp (ca. 20-22°C) or 18hrs if using a longer fermentation process. You can use the poke test as an indicator of your dough development.',
      'Make sure to feed your starter 3 times in the 24hrs before using it. This will greatly improve its vitality and leavening ability, whilst also allowing for a less acidic flavour.',
      'Adjust quantity of water in your recipe to the type of flour. Normal hydration rate is often close to 70% but some flours ask for even more. Flours with higher protein content (bread flour) can better handle increased hydration. If you are using mostly whole wheat or lower-protein flour (eg. ancient wheat varieties), the crumb can also be tighter. Mixing these flours with 1/3 of white strong flour usually allows for a more open crumb while preserving their unique flavours.',
      'Handle the dough gently during shaping to preserve the air pockets formed during bulk fermentation. The less you manipulate your dough, the more open the crumb will be.',
      'Consider doing an autolyse before kneading, especially if using a strong white flour. Mix flour + water and let this dough rest for 1h. This process will help soften the dough and allow for a better elasticity.'
    ]
  },
  {
    name: 'Cavernous Holes',
    visualIdentifiers: ['Large, uneven air pockets', 'concentrated near top of loaf'],
    mainCauses: [
      'Overhydration',
      'Weak shaping',
      'Insufficient gluten development',
      'Lack of fermentation (dough baked too early)'
    ],
    solutions: [
      'Decrease water ratio. Aim for a 70% hydration rate, but for some white flour, this can still be too much. Check the texture when kneading. If the dough spreads across your container, try incorporating 50-100g of flour.',
      'Strengthen gluten through proper kneading techniques. Consider using stretch and fold technique.',
      'Improve shaping to create more surface tension in your loaf. This will help generate a better oven spring and a better crumb development.',
      'Increase fermentation time. Try to stick around 6-7hrs of total fermentation time at room temp or around 18hrs if using your fridge. This will allow for a more even crumb.'
    ]
  },
  {
    name: 'Flat Loaf',
    visualIdentifiers: ['Little oven spring', 'collapsed structure', 'poor rise'],
    mainCauses: [
      'Overproofing',
      'Weak shaping',
      'Weak gluten development',
      'Dough too hydrated'
    ],
    solutions: [
      'Shorten proofing time. As your sourdough ferments, it becomes more and more fragile until it cannot hold its shape anymore. Try to stick around 6-7hrs of total fermentation time at room temp or around 18hrs if using your fridge.',
      'Improve shaping with surface tension techniques. This will help generate a better oven spring and a better crumb development.',
      'Strengthen gluten through proper kneading techniques. Consider using stretch and fold technique.',
      'Reduce hydration of dough. Aim for a 70% hydration rate, but for some white flour, this can still be too much. Check the texture when kneading. If the dough spreads across your container, try incorporating 50-100g of flour or reducing the quantity of water in your recipe.'
    ]
  },
  {
    name: 'Sticky Dough',
    visualIdentifiers: ['Wet', 'difficult to handle', 'loses shape easily'],
    mainCauses: [
      'Overhydration',
      'Overproofing',
      'Poor shaping'
    ],
    solutions: [
      'Reduce hydration of dough. Aim for a 70% hydration rate, but for some white flour, this can still be too much. Check the texture when kneading. If the dough spreads across your container, try incorporating 50-100g of flour or reducing the quantity of water in your recipe.',
      'Use cold-proofing to slow fermentation and firm dough. Using cold-retard fermentation technique will allow for a better texture as well as a more open crumb! Try placing your dough in the fridge for 15hrs before baking it.',
      'Minimize handling. When shaping a sticky dough, the less you touch it or the faster you are, the less it will have time to stick to your hands! Don\'t let the dough decide, you\'re the chef!'
    ]
  },
  {
    name: 'Burnt or Pale Crust',
    visualIdentifiers: ['Dark, charred crust or light', 'underdeveloped crust'],
    mainCauses: [
      'Oven temperature too high/low',
      'Poor steam injection',
      'Baking time too short/long'
    ],
    solutions: [
      'Adjust oven temperature to 220-240°C (430-465°F). This will allow for the bread to properly develop before the baked crust around it prevents any further oven spring. Your bread will need a little time to be properly cooked through as we need the center of the dough to reach ca. 94°C. A very high baking temperature will result in a burnt crust before the core of your bread reaches this temperature',
      'Ensure adequate steaming at the start of the bake. This is absolutely crucial as the bread needs humidity at the start of the baking process for the crust to form properly and the bread to develop nicely.',
      'Monitor baking duration. Coloration usually appears toward the end of baking so you need to keep an eye on your bread as time passes. If you feel like your bread lacks coloration, consider increasing you oven temperature by 5-10°C (40-50°F).'
    ]
  },
  {
    name: 'Gummy Texture',
    visualIdentifiers: ['Sticky interior', 'knife sticks when slicing'],
    mainCauses: [
      'Underproofing',
      'Insufficient baking',
      'Cutting bread before cooling'
    ],
    solutions: [
      'Lengthen proofing time. A too short fermentation time often results in a gummy texture as if the bread wasn\'t cooked through. Try to allow enough time for your dough to ferment properly: 6-7hrs if using direct fermentation process or ca. 18hrs for longer options.',
      'Bake until loaf reaches 200°F (94°C) internally. This will make sure that the crumb is properly cooked through and it has the right texture.',
      'Allow bread to cool fully before slicing it. We know it smells wonderful and you probably can\'t wait to taste your bread but we need to wait for all the remaining humidity in the crumb to migrate outisde the bread. This process usually takes about 1hr. After this, you will be able to slice your bread much more easily!'
    ]
  }
];

// Process Parameters Guide
interface ProcessParameter {
  name: string;
  idealRange: string;
  tooLow: string[];
  tooHigh: string[];
  specialConsiderations: string;
}

const PROCESS_PARAMETERS: ProcessParameter[] = [
  {
    name: 'Hydration Level',
    idealRange: '65-80%',
    tooLow: ['Dry dough', 'tight crumb'],
    tooHigh: ['Sticky', 'unmanageable dough'],
    specialConsiderations: 'Adjust hydration to flour type. The more bran in the flour, the more water will be absorbed.'
  },
  {
    name: 'Fermentation Time',
    idealRange: '6-8 hours',
    tooLow: ['Dense, gummy crumb', 'underdeveloped flavor'],
    tooHigh: ['Weak structure', 'collapsed loaf'],
    specialConsiderations: 'Use poke test or dough doubling as benchmark'
  },
  {
    name: 'Proofing Temperature',
    idealRange: '18-25°C (70-82°F)',
    tooLow: ['Small, dense loaf', 'final bread very pale'],
    tooHigh: ['Flattened, deflated loaf', 'very acidic taste'],
    specialConsiderations: 'Monitor room conditions and adjust accordingly'
  }
];

// Technique Guides
interface TechniqueGuide {
  name: string;
  keyPoints: string[];
  commonMistakes: string[];
  checkpoints: string[];
}

const TECHNIQUE_GUIDES: TechniqueGuide[] = [
  {
    name: 'Shaping',
    keyPoints: [
      'Lightly flour hands and bench',
      'Form dough into a taut shape',
      'Be gentle to preserve gas bubbles'
    ],
    commonMistakes: [
      'Degassing dough',
      'Uneven shaping',
      'Too much pressure when shaping'
    ],
    checkpoints: [
      'Dough holds shape and retains gas pockets'
    ]
  },
  {
    name: 'Scoring',
    keyPoints: [
      'Use sharp blade at 45° angle',
      'Create at least one deep slash',
      'Score in one quick motion'
    ],
    commonMistakes: [
      'Too shallow scoring',
      'Blade pulls dough',
      'Going too slow'
    ],
    checkpoints: [
      'Smooth quick cut with a sharp blade'
    ]
  }
];

interface AnalysisInput {
  ratings: {
    crumbStructure: number;
    ovenSpring: number;
    crust: number;
    crumbTexture: number;
  };
  parameters: {
    fermentationType: string;
    fermentationDuration: number;
    bakingMethod: string;
    temperature: number;
    weight: number;
    bakingDuration: number;
    hydration: number;
  };
}

function getBasicAnalysis(ratings: AnalysisInput['ratings']) {
  // Find the lowest-rated aspect
  const lowestRating = Math.min(
    ratings.crumbStructure,
    ratings.ovenSpring,
    ratings.crust,
    ratings.crumbTexture
  );

  // If all ratings are above 70, return no major issues
  if (lowestRating > 70) {
    return {
      mainIssue: '',
      causes: [],
      solutions: []
    };
  }

  // Map ratings to potential problems
  let problem: BreadProblem | undefined;

  if (ratings.crumbStructure < 60) {
    problem = BREAD_PROBLEMS.find(p => p.name === 'Dense Crumb');
  } else if (ratings.ovenSpring < 60) {
    problem = BREAD_PROBLEMS.find(p => p.name === 'Flat Loaf');
  } else if (ratings.crust < 60) {
    problem = BREAD_PROBLEMS.find(p => p.name === 'Burnt or Pale Crust');
  } else if (ratings.crumbTexture < 60) {
    problem = BREAD_PROBLEMS.find(p => p.name === 'Gummy Texture');
  }

  if (!problem) {
    return {
      mainIssue: '',
      causes: [],
      solutions: []
    };
  }

  return {
    mainIssue: problem.name,
    causes: problem.mainCauses,
    solutions: problem.solutions
  };
}

function analyzeBreadIssues(input: AnalysisInput) {
  const issues = getBasicAnalysis(input.ratings);
  return {
    mainIssue: issues.mainIssue,
    causes: issues.causes,
    solutions: issues.solutions
  };
}

function generateMethodologyFeedback(parameters: AnalysisInput['parameters']) {
  const feedback: string[] = [];

  // Check fermentation duration based on process type
  if (parameters.fermentationType === 'direct') {
    if (parameters.fermentationDuration > 8) {
      feedback.push('Your fermentation time might be too long for direct fermentation. This often results in the gluten network to be weakened by the enzymes. Your bread will lack volume and also have a much more acidic taste. Consider reducing your fermentation time to 6-7 hours at room temperature.');
    }
    else if (parameters.fermentationDuration < 6) {
      feedback.push('Your fermentation time might be too short for direct fermentation. This often results in a denser crumb. Your bread will lack volume and won\'t have enough time to develop its flavour. Consider increasing your fermentation time to 6-7 hours at room temperature.');
    }
  } else {
    // For cold-bulk, slow-room, and cold-retard processes
    const targetDuration = 18; // Target duration for long fermentation processes

    if (parameters.fermentationDuration < targetDuration) {
      feedback.push('Your fermentation duration might be too short for this process. Long fermentation methods typically require around 18 hours to develop proper flavor and structure. This allows the dough to develop complex flavors and improve digestibility.');
    } else if (parameters.fermentationDuration > targetDuration + 6) { // Allow some flexibility up to 24 hours
      feedback.push('Your fermentation duration might be a little too long. If using a fridge, make sure it is cold enough (around 4°C/39°F) to completely pause fermentation. At higher temperatures, the dough might over-ferment, leading to a weakened structure and overly sour taste.');
    }
  }

  // Check hydration based on fermentation type
  const getHydrationRange = (fermentationType: string) => {
    switch (fermentationType) {
      case 'direct':
        return { min: 65, max: 80 };
      case 'cold-bulk':
        return { min: 70, max: 90 };
      case 'slow-room':
        return { min: 65, max: 80 };
      case 'cold-retard':
        return { min: 70, max: 100 };
      default:
        return { min: 65, max: 80 }; // Default range
    }
  };

  const hydrationRange = getHydrationRange(parameters.fermentationType);
  
  if (parameters.hydration < hydrationRange.min) {
    feedback.push(`Your hydration rate might be too low for ${parameters.fermentationType} process. This often results in a dry dense crumb. For this fermentation method, try to maintain hydration between ${hydrationRange.min}% and ${hydrationRange.max}%.  Don\'t be afraid of a sticky dough. This is normal and even necessary as we need the dough to be soft enough for the bubbles inside to be able to form. Consider gradually increasing the quantity of water in your recipe by 2% at a time.`);
  } else if (parameters.hydration > hydrationRange.max) {
    feedback.push(`Your hydration rate (${parameters.hydration}%) might be too high for ${parameters.fermentationType} fermentation process. While high hydration can lead to an open crumb, it can also make the dough difficult to handle. For this method, try to stay between ${hydrationRange.min}% and ${hydrationRange.max}%. However, watch your dough, not the numbers. Check the texture when kneading and see if the dough can hold its shape or spreads across your container. Whilst this is acceptable for cold fermentation processes, it might be tricky for fermentations at room temperature. If so, consider reducing the water content by 2-3% to improve dough handling while maintaining good fermentation.`);
  }

  // Check temperature
  if (parameters.temperature < 220) {
    feedback.push('Your baking temperature might be too low. Consider increasing it to ca. 240°C for better crust development. Your bread will need a certain heat for the crust to caramelize. This will also allow for the baking duration to be shorter, allowing for less dehydration of the dough during baking process and ultimately a better conservation time for your bread.');
  } else if (parameters.temperature > 260) {
    feedback.push('Very high baking temperature. Make sure to monitor the crust carefully to prevent burning. Consider reducing it to ca. 240°C for better crust development. This will also allow the bread to properly develop before the baked crust around it prevents any further oven spring. Your bread will need a little time to be properly cooked through as we need the center of the dough to reach ca. 94°C. A very high baking temperature will result in a burnt crust before the core of your bread reaches this temperature.');
  }

  // Check baking duration based on weight and oven type
  const calculateExpectedDuration = (weight: number, ovenType: string): number => {
    if (ovenType === 'wood-fired') {
      // Wood-fired oven durations
      if (weight >= 3) {
        return 90; // 1.5 hours for 3kg
      } else if (weight >= 2) {
        return 60; // 1 hour for 2kg
      } else {
        // For weights under 2kg: 40min per kilo, minimum 20 minutes
        return Math.max(20, Math.round(weight * 40));
      }
    } else {
      // Regular oven durations
      if (weight >= 3) {
        return 120; // 2 hours for 3kg or more
      } else if (weight >= 2) {
        return 90; // 1.5 hours for 2kg
      } else if (weight >= 1) {
        return 60; // 1 hour for 1kg
      } else {
        // For weights under 1kg, calculate proportionally
        // but ensure minimum of 35 minutes
        return Math.max(35, Math.round(weight * 60));
      }
    }
  };

  const expectedDuration = calculateExpectedDuration(parameters.weight, parameters.bakingMethod);
  
  if (Math.abs(parameters.bakingDuration - expectedDuration) > 10) {
    const durationMessage = parameters.weight >= 1
      ? `${Math.round(expectedDuration / 60)} hour${expectedDuration >= 120 ? 's' : ''}`
      : `${expectedDuration} minutes`;
      
    const ovenSpecificMessage = parameters.bakingMethod === 'wood-fired'
      ? `For wood-fired ovens, we recommend ${parameters.weight >= 2 ? 'shorter baking times' : '40 minutes per kilo'} due to the higher temperatures and better heat distribution.`
      : `For most domestic ovens, we generally allow for 1 hour per kilo of bread, with adjustments for larger loaves: 1.5 hours for 2kg and 2 hours for 3kg loaves.`;
    
    feedback.push(
      `Based on your bread weight (${parameters.weight}kg) and oven type, consider adjusting baking time to around ${durationMessage}. ${ovenSpecificMessage}`
    );
  }

  return feedback;
}

function generateImprovementSuggestions(
  ratings: AnalysisInput['ratings'],
  parameters: AnalysisInput['parameters']
) {
  const suggestions: string[] = [];

  // Get the main issue first
  const { mainIssue } = getBasicAnalysis(ratings);

  // Suggest improvements based on ratings
  if (ratings.crumbStructure < 70) {
    suggestions.push('If you feel like your dough was very soft and you had trouble shaping it and keeping it in place, work on developing gluten strength through proper kneading and folding techniques.');
  }

  if (ratings.ovenSpring < 70) {
    if (parameters.bakingMethod === 'dutch-oven') {
      suggestions.push('Ensure your Dutch oven is properly sealed during the first 30 minutes of baking process. If you still find the crust too thick or pale, you can try creating more steam by spraying water before closing the lid. Also make sure that your dutch-oven is not too large for your bread as we want the steam coming out from the dough to completely fill the dutch-oven.');
    } else if (parameters.bakingMethod !== 'wood-fired') {
      suggestions.push('Consider using a Dutch oven or creating more steam in your oven for better oven spring.');
    }
  }

  if (ratings.crust < 70) {
    if (parameters.bakingMethod === 'dutch-oven') {
      suggestions.push('Ensure your Dutch oven is properly sealed during the first 30 minutes of baking process. If you still find the crust too thick or pale, you can try creating more steam by spraying water before closing the lid. Also make sure that your dutch-oven is not too large for your bread as we want the steam coming out from the dough to completely fill the dutch-oven.');
    } else if (parameters.bakingMethod !== 'wood-fired') {
      suggestions.push('Consider using a Dutch oven or creating more steam in your oven for a better crust. The adequate amount will ultimately depend on your oven, its ability to keep humidity trapped inside, or the heat distribution and circulation inside it. Some oven ask for a water-filled dish under the bread, but others will need the loaf to be sprayed on top of this. Check the texture of your crust. If it looks like it has been through a sand storm, then consider adding more humidity in your oven.');
    }
  }

  // Only add crumb texture suggestion if the main issue is not Gummy Texture
  if (ratings.crumbTexture < 70 && mainIssue !== 'Gummy Texture') {
    suggestions.push('Allow bread to cool completely before slicing to prevent gummy texture. We know it smells wonderful and you probably can\'t wait to taste your bread but we need to wait for all the remaining humidity in the crumb to migrate outisde the bread. This process usually takes about 1hr. After this, you will be able to slice your bread much more easily!');
  }

  // Add technique-specific suggestions
  if (parameters.fermentationType === 'cold-bulk') {
    suggestions.push('For cold bulk fermentation, ensure dough temperature is below 40°F (4°C) during the bulk phase.');
  }

  return suggestions;
}

export { analyzeBreadIssues, generateMethodologyFeedback, generateImprovementSuggestions }